import {Brand} from "@mytour/platform";
import RevolutCheckout from "@revolut/checkout";
import * as Sentry from "@sentry/react";
import {useEffect} from "react";
import {toast} from "react-toastify";
import {Modal, ModalBody, ModalHeader} from "reactstrap";

import type {NewSubscriptionPayload} from "@mytour/shared";
import {Loading} from "@mytour/ui/Loading";
import axios from "axios";

type Props = {
  onAbort: () => void;
  onPay: () => void;
  type: "monthly" | "yearly";
  existingOrderId?: string | null;
  revolutMode?: "prod" | "sandbox" | "dev";
};

export function RevolutModal({onAbort, onPay, type, existingOrderId, revolutMode}: Props) {
  useEffect(() => {
    pay();
  }, []);

  return (
    <Modal isOpen={true} backdrop="static" autoFocus={false} centered>
      <ModalHeader close={<></>} className="mytour-header">
        <Brand />
        {"Paiement"}
      </ModalHeader>
      <ModalBody>
        <p style={{textAlign: "center"}}>
          <b>{"Mise en place du formulaire de paiement"}</b>
        </p>
        <Loading />
      </ModalBody>
    </Modal>
  );

  async function pay() {
    try {
      let publicId: string;
      let mode: "prod" | "sandbox" | "dev";
      let savePaymentMethodFor: "merchant" | "customer";
      let options: NewSubscriptionPayload["options"];

      if (existingOrderId) {
        // Retry: reuse existing Revolut order — skip POST /api/order/
        publicId = existingOrderId;
        mode = revolutMode ?? "prod";
        savePaymentMethodFor = "merchant";
        options = {locale: "fr"};
      } else {
        // New order
        const response = await axios.post<NewSubscriptionPayload>("/api/order/", {type});
        ({publicId, mode, savePaymentMethodFor, options} = response.data);
      }

      const RC = await RevolutCheckout(publicId, mode as "prod" | "sandbox");

      RC.payWithPopup({
        ...options,
        savePaymentMethodFor,
        onSuccess() {
          // Always call firstPayment — it syncs Revolut state and creates
          // the invoice/Event when one doesn't exist yet (e.g. newSubscription
          // order that the user cancelled then retried via the modal).
          axios
            .post(`/api/order/${publicId}/payment/`)
            .then(() => {
              onPay();
            })
            .catch(handlePostSuccessConfirmationError);
        },
        onError: handlePayError,
        onCancel() {
          onAbort();
        },
      });
    } catch (error) {
      /**
       * Couvre la création de commande et l'ouverture du checkout Revolut — le popup de
       * paiement lui-même a son propre onError, ci-dessus.
       */
      handlePayError(error);
    }
  }

  function handlePayError(error?: unknown) {
    toast.error("Erreur pendant le paiement");
    onAbort();
    if (error !== undefined) {
      Sentry.captureException(error, {tags: {source: "revolut-modal-pay"}});
    }
  }

  /**
   * Échec du POST de confirmation qui suit un `onSuccess` du popup Revolut — la carte a
   * déjà été débitée à ce stade, contrairement à `handlePayError` (échec avant capture).
   * Le message ne doit donc jamais laisser croire qu'aucun débit n'a eu lieu. Pas d'état UI
   * dédié à la réconciliation post-capture : le retour au formulaire (`onAbort`) reste
   * identique à `handlePayError`, seul le texte diffère.
   */
  function handlePostSuccessConfirmationError(error: unknown) {
    toast.error(
      "Votre paiement a été accepté mais nous n'avons pas pu le confirmer. Contactez le support si le problème persiste."
    );
    onAbort();
    Sentry.captureException(error, {tags: {source: "revolut-modal-post-success-confirmation"}});
  }
}
